import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Upload, Mic, Play, Pause, Trash2, Download, Search, Filter,
  Volume2, ArrowLeft, RefreshCw, FileAudio, Plus, StopCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

const CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'general', label: 'General' },
  { value: 'greeting', label: 'Greetings' },
  { value: 'error', label: 'Error Messages' },
  { value: 'prompt', label: 'Prompts' },
  { value: 'tts', label: 'Generated TTS' }
];

export default function AudioLibrary() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [agent, setAgent] = useState(null);
  const [audioFiles, setAudioFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // TTS Generation
  const [showTTSModal, setShowTTSModal] = useState(false);
  const [ttsText, setTtsText] = useState('');
  const [ttsName, setTtsName] = useState('');
  const [ttsVoice, setTtsVoice] = useState('Rachel');
  const [ttsProvider, setTtsProvider] = useState('elevenlabs');
  const [voices, setVoices] = useState({ elevenlabs: [], openai: [] });
  
  // Audio player ref
  const audioRef = useRef(null);

  useEffect(() => {
    loadData();
    loadVoices();
  }, [agentId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const agentRes = await api.get(`/agents/${agentId}`);
      setAgent(agentRes.data.agent || agentRes.data);
      
      const audioRes = await api.get(`/ivr/${agentId}/audio`);
      setAudioFiles(audioRes.data.data || []);
    } catch (error) {
      console.error('Load audio error:', error);
      toast.error('Failed to load audio library');
    } finally {
      setLoading(false);
    }
  };

  const loadVoices = async () => {
    try {
      const res = await api.get(`/ivr/${agentId}/audio/voices`);
      setVoices(res.data.data || { elevenlabs: [], openai: [] });
    } catch (error) {
      console.error('Load voices error:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('audio', file);
      formData.append('name', file.name.replace(/\.[^/.]+$/, ''));
      formData.append('category', 'general');
      
      await api.post(`/ivr/${agentId}/audio/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Audio uploaded successfully');
      loadData();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload audio');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleGenerateTTS = async () => {
    if (!ttsText.trim()) {
      toast.error('Please enter text to convert');
      return;
    }
    
    try {
      setGenerating(true);
      
      await api.post(`/ivr/${agentId}/audio/generate`, {
        text: ttsText,
        name: ttsName || `TTS: ${ttsText.substring(0, 30)}...`,
        voice: ttsVoice,
        provider: ttsProvider,
        category: 'tts'
      });
      
      toast.success('Audio generated successfully');
      setShowTTSModal(false);
      setTtsText('');
      setTtsName('');
      loadData();
    } catch (error) {
      console.error('Generate TTS error:', error);
      toast.error('Failed to generate audio: ' + (error.response?.data?.error || error.message));
    } finally {
      setGenerating(false);
    }
  };

  const handlePlay = (audioId) => {
    if (playingId === audioId) {
      // Stop playing
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingId(null);
    } else {
      // Stop previous
      if (audioRef.current) {
        audioRef.current.pause();
      }
      
      // Play new
      const audio = new Audio(`${api.defaults.baseURL}/ivr/${agentId}/audio/${audioId}/stream`);
      audio.onended = () => setPlayingId(null);
      audio.onerror = () => {
        toast.error('Failed to play audio');
        setPlayingId(null);
      };
      audio.play();
      audioRef.current = audio;
      setPlayingId(audioId);
    }
  };

  const handleDelete = async (audioId) => {
    if (!confirm('Are you sure you want to delete this audio?')) return;
    
    try {
      await api.delete(`/ivr/${agentId}/audio/${audioId}`);
      toast.success('Audio deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete audio');
    }
  };

  const filteredAudio = audioFiles.filter(audio => {
    const matchesSearch = !searchQuery || 
      audio.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      audio.tts_text?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || audio.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/agents/${agentId}/ivr`)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Audio Library</h1>
            <p className="text-gray-600">{agent?.name}</p>
          </div>
        </div>
        
        <div className="flex gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/mp3,audio/wav,audio/mpeg"
            onChange={handleFileUpload}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {uploading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            Upload Audio
          </button>
          <button
            onClick={() => setShowTTSModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Mic className="w-4 h-4" />
            Generate TTS
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search audio files..."
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-4 py-2 border rounded-lg"
        >
          {CATEGORIES.map(cat => (
            <option key={cat.value} value={cat.value}>{cat.label}</option>
          ))}
        </select>
      </div>

      {/* Audio Grid */}
      {filteredAudio.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <FileAudio className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No audio files</h3>
          <p className="text-gray-500 mb-4">
            Upload audio files or generate them using text-to-speech
          </p>
          <div className="flex justify-center gap-3">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Upload Audio
            </button>
            <button
              onClick={() => setShowTTSModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Generate TTS
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAudio.map(audio => (
            <div key={audio.id} className="bg-white rounded-xl shadow-sm border p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-gray-900 truncate">{audio.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      audio.source === 'tts' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {audio.source === 'tts' ? 'TTS' : 'Upload'}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatFileSize(audio.file_size)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handlePlay(audio.id)}
                    className={`p-2 rounded-lg ${
                      playingId === audio.id
                        ? 'bg-blue-100 text-blue-600'
                        : 'hover:bg-gray-100 text-gray-600'
                    }`}
                  >
                    {playingId === audio.id ? (
                      <Pause className="w-4 h-4" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={() => handleDelete(audio.id)}
                    className="p-2 hover:bg-red-50 rounded-lg text-gray-600 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {audio.tts_text && (
                <p className="text-sm text-gray-500 line-clamp-2 mb-2">
                  "{audio.tts_text}"
                </p>
              )}
              
              <div className="flex items-center justify-between text-xs text-gray-400">
                <span>{audio.category || 'general'}</span>
                <span>{new Date(audio.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* TTS Generation Modal */}
      {showTTSModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">Generate Audio from Text</h2>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Text to Convert *
                </label>
                <textarea
                  value={ttsText}
                  onChange={(e) => setTtsText(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  rows={4}
                  placeholder="Enter the text you want to convert to speech..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Audio Name (optional)
                </label>
                <input
                  type="text"
                  value={ttsName}
                  onChange={(e) => setTtsName(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="e.g., Welcome Message"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Provider
                  </label>
                  <select
                    value={ttsProvider}
                    onChange={(e) => {
                      setTtsProvider(e.target.value);
                      const providerVoices = voices[e.target.value] || [];
                      if (providerVoices.length > 0) {
                        setTtsVoice(providerVoices[0].id);
                      }
                    }}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="elevenlabs">ElevenLabs</option>
                    <option value="deepgram">Deepgram Aura</option>
                    <option value="openai">OpenAI</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Voice
                  </label>
                  <select
                    value={ttsVoice}
                    onChange={(e) => setTtsVoice(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    {(voices[ttsProvider] || []).map(voice => (
                      <option key={voice.id} value={voice.id}>{voice.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-700">
                <strong>Tip:</strong> Keep text concise for better audio quality. 
                Generated audio will be saved to your library for reuse.
              </div>
            </div>
            
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => { setShowTTSModal(false); setTtsText(''); setTtsName(''); }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateTTS}
                disabled={generating || !ttsText.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {generating ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Mic className="w-4 h-4" />
                )}
                Generate Audio
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}